import {Extension} from "@/types/extenions.ts";
import {api} from "@/lib/utils.ts";
import {toast} from "sonner";
import React, {ComponentType, useMemo} from "react";
import {DocumentDuplicateIcon, Square2StackIcon, TrashIcon} from "@heroicons/react/24/outline";
import {useDropExtensionMutation} from "@/store/extensions.ts";
import {setCopyAdvancedSettings, setDuplicateExtension} from "@/store/extensionsPageActions.ts";
import {useDispatch} from "@/store";

export const allowSubExtensions = ['LMENU', 'GT', 'SAEVE', 'TICKA'];
export const allowAddAudioFile = ['MB','ONMSG','CLS', 'LIVEF'];
export const allowAddHebAudioFile = ['HEBD',];
export const allowShowFilesList = ['MB','ONMSG','CLS', 'LIVEF', 'CONF'];
export const allowShowHebFilesList = ['HEBD',];
export const allowShowUserRegister = ['MB', 'ONMSG', 'CLS', 'MSGS', 'PMSG', 'CONF', 'RTZ', 'LIVEF', 'CONF', 'LMENU', 'GT'];
export const allowShowRecordings = [];
export const allowSendShortCall = ['MB', 'HEBD'];
export const allowSendShortCallOnConf = ['CLS', 'CONF'];
export const allowManageConf = ['CLS', 'CONF'];
export const allowAddFileToConf = ['CLS', 'CONF'];
export const allowShowConfLog = ['CLS', 'CONF'];
export const allowShowCreditCardTransactions = ['PCRED'];
export const allowShowResults = ['RNU', 'FORM'];
export const allowShowListenLog = ['MB', 'ONMSG', 'HEBD'];
export const allowShowFaxLog = ['REFAX'];
export const allowShowFaxList = ['DOC'];
export const allowAddAgentToQueue = ['AG', 'DIQ'];
export const requiredOpenMsg = ['LMENU'];

function allow(ext: Extension, types: string[]) {
    return !!(ext.cls_type && types.includes(ext.cls_type));
}
export interface ConfirmProperties {
    message: string;
    variant: string;
    title: string;
    confirmText: string;
    cancelText: string;
}
export interface Action {
    name: string;
    action: string;
    description: string;
    types: string[];
    allow: (ext: Extension) => boolean;
    //call can be return async axios
    call? : (ext: Extension, onSuccess?: () => void) => Promise<any>;
    href?: (ext: Extension) => string;
    confirm?: (ext: Extension) => ConfirmProperties;
    //teh type of DocumentDuplicateIcon or Square2StackIcon
    icon?: React.ComponentType;
    disabled?: boolean
}

interface ExtensionActions extends Array<Action> {
    filterExt: (ext: Extension) => Action[];
}

const extensionActions: ExtensionActions = [
    {
        name: 'הוספת קובץ שמע',
        action: 'add_audio_file',
        description: 'action',
        types: allowAddAudioFile,
        allow: (ext) => allow(ext, allowAddAudioFile),
        call: (ext) => {
            window.open(`/learning/streamfilesclass/add/?_popup=1&sclass=${ext.id}&type=MSG`, 'popup', 'width=800,height=600');
        },
    },
    {
        name: 'הוספת קובץ שמע עם תאריך עברי',
        action: 'add_heb_audio_file',
        description: 'action',
        types: allowAddHebAudioFile,
        allow: (ext) => allow(ext, allowAddHebAudioFile),
        call: (ext) => {
            window.open(`/learning/streamfilehebdate/add/?_popup=1&sclass=${ext.id}&type=MSG`, 'popup', 'width=800,height=600');
        },
    },
    {
        name: 'העלאת קבצים מרובים',
        action: 'add_multiple_files',
        description: 'action',
        types: allowAddAudioFile,
        allow: (ext) => allow(ext, allowAddAudioFile),
        call: (ext) => {
            window.open(`/learning/streamfilemultiupload/add/?_popup=1&sclass_list=${ext.id}&type=MSG`, 'popup', 'width=800,height=600');
        },
    },
    {
        name: 'צפיה ברשימת הקבצים',
        action: 'show_files_list',
        description: 'view',
        types: allowShowFilesList,
        allow: (ext: Extension) => allow(ext, allowShowFilesList),
        href: (ext: Extension) => `/learning/streamfilesclass/?_sclass=${ext.id}`,
    },
    {
        name: 'צפיה ברשימת הקבצים',
        action: 'show_heb_files_list',
        description: 'view',
        types: allowShowHebFilesList,
        allow: (ext: Extension) => allow(ext, allowShowHebFilesList),
        href: (ext: Extension) => `/learning/streamfilehebdate/?_sclass=${ext.id}`,
    },
    {
        name: 'צפיה ברשימת ההקלטות בשלוחה',
        action: 'show_recordings_list',
        description: 'view',
        href: (ext: Extension) => `/learning/streamfilesclass/?_sclass=${ext.id}`,
        types: allowShowRecordings,
        allow: (ext: Extension) => allow(ext, allowShowRecordings)
    },
    {
        name: 'צפיה במשתמשים הרשומים בשלוחה',
        action: 'show_user_register_list',
        description: 'view',
        href: (ext: Extension) => `/learning/sclassuser/?_sclass=${ext.id}`,
        types: allowShowUserRegister,
        allow: (ext: Extension) => allow(ext, allowShowUserRegister)
    },
    {
        name: 'שלח צינתוק',
        action: 'send_short_call',
        description: 'action',
        types: allowSendShortCall,
        call: async (ext: Extension, onSuccess?: () => void) => {
            return await api.post(`/system/sclass/${ext.id}/send_zintok/`)
                .then(() => toast.success('הצינתוק נשלח בהצלחה') && onSuccess?.())
                .catch(() => toast.error('שגיאה בשליחת הצינתוק'));
        },
        confirm: (ext: Extension) => ({
            message: `האם אתה בטוח שברצונך לשלוח צינתוק למשתמשים משלוחה ${ext.name}?`,
            variant: 'default',
            title: 'שלח צינתוק',
            confirmText: 'שלח צינתוק',
            cancelText: 'ביטול',
        }),
        allow: (ext: Extension) => allow(ext, allowSendShortCall)
    },
    {
        name: 'שלח צינתוק על התחלת ועידה',
        action: 'send_short_call_on_conf_start',
        description: 'action',
        types: allowSendShortCallOnConf,
        allow: (ext: Extension) => allow(ext, allowSendShortCallOnConf),
        call: async (ext: Extension, onSuccess?: () => void) => {
            return await api.post(`/system/sclass/${ext.id}/send_zintok_conf/`)
                .then(() => toast.success('הצינתוק נשלח בהצלחה') && onSuccess?.())
                .catch(() => toast.error('שגיאה בשליחת הצינתוק'));
        },
        confirm: (ext: Extension) => ({
            message: `האם אתה בטוח שברצונך לשלוח צינתוק למשתמשים מחדר הועידה בשלוחה ${ext.name}?`,
            variant: 'default',
            title: 'שלח צינתוק מועידה',
            confirmText: 'שלח צינתוק',
            cancelText: 'ביטול',
        }),
    },
    {
        name: 'ניהול ועידה בזמן אמת',
        action: 'manage_conf_real_time',
        description: 'edit',
        types: allowManageConf,
        allow: (ext: Extension) => allow(ext, allowManageConf),
        href: (ext: Extension) => `/learning/liveconfbridgeuserlog/?_sclass=${ext.id}`,
    },
    {
        name: 'הוספת קובץ לועידה',
        action: 'add_file_to_conf',
        description: 'action',
        href: (ext: Extension) => `/learning/streamfilesclass/?type__exact=FFC&conf_id=${ext.id}`,
        types: allowAddFileToConf,
        allow: (ext: Extension) => allow(ext, allowAddFileToConf)
    },
    {
        name: 'צפיה ביומן שיחות ועידה',
        action: 'show_conf_log',
        description: 'view',
        types: allowShowConfLog,
        href: (ext: Extension) => `/learning/confbridgeuserlog/?_sclass=${ext.id}`,
        allow: (ext: Extension) => allow(ext, allowShowConfLog)
    },
    {
        name:'צפיה בעיסקאות אשראי',
        action:'show_credit_card_transactions',
        description:'view',
        types: allowShowCreditCardTransactions,
        allow: (ext: Extension) => allow(ext, allowShowCreditCardTransactions),
        href: (ext: Extension) => `/learning/result/?sclass__id__exact=${ext.id}&fields=%D7%9E%D7%A1%D7%A4%D7%A8%20%D7%90%D7%A9%D7%A8%D7%90%D7%99,%D7%AA%D7%95%D7%A7%D7%A3%20%D7%90%D7%A9%D7%A8%D7%90%D7%99,CVV,%D7%9E%D7%A1%D7%A4%D7%A8%20%D7%96%D7%94%D7%95%D7%AA,%D7%A1%D7%95%D7%92%20%D7%A2%D7%A1%D7%A7%D7%94,%D7%A1%D7%9B%D7%95%D7%9D,%D7%AA%D7%A9%D7%9C%D7%95%D7%9E%D7%99%D7%9D,%D7%9E%D7%A1%D7%A4%D7%A8%20%D7%A2%D7%A1%D7%A7%D7%94,%D7%94%D7%A7%D7%9C%D7%98%D7%AA%20%D7%A9%D7%9D,%D7%94%D7%A7%D7%9C%D7%98%D7%AA%20%D7%9B%D7%AA%D7%95%D7%91%D7%AA,%D7%98%D7%9C%D7%A4%D7%95%D7%9F%20%D7%9C%D7%91%D7%99%D7%A8%D7%95%D7%A8%D7%99%D7%9D,%D7%98%D7%9C%D7%A4%D7%95%D7%9F%20%D7%9E%D7%A2%D7%A8%D7%9B%D7%AA,%D7%9E%D7%A1%D7%95%D7%A3,%D7%A1%D7%98%D7%98%D7%95%D7%A1,%D7%9B%D7%9E%D7%95%D7%AA`
    },
    {
        name:'צפיה בתוצאות',
        action:'show_results',
        description:'view',
        types: allowShowResults,
        allow: (ext: Extension) => allow(ext, allowShowResults),
        href: (ext: Extension) => `/learning/result/?_sclass=${ext.id}`
    },
    {
        name:'צפיה ביומן שמיעת הקלטות',
        action:'show_listen_log',
        description:'view',
        types: allowShowListenLog,
        allow: (ext: Extension) => allow(ext, allowShowListenLog),
        href: (ext: Extension) => `/learning/streamfileuser/?stream_file_class__sclass=${ext.id}`
    },
    {
        name:'צפיה ביומן פקסים',
        action:'show_fax_log',
        description:'view',
        types: allowShowFaxLog,
        allow: (ext: Extension) => allow(ext, allowShowFaxLog),
        href: (ext: Extension) => `/learning/result/?_sclass=${ext.id}`
    },
    {
        name:'רשימת מסמכים לפקס',
        action:'files_doc_list',
        description:'view',
        types: allowShowFaxList,
        allow: (ext: Extension) => allow(ext, allowShowFaxList),
        href: (ext: Extension) => `/learning/filesclass/?sclass=${ext.id}`
    },
    {
        name:'צפיה ביומן פקסים שנשלחו',
        action:'fax_list',
        description:'view',
        types: allowShowFaxList,
        allow: (ext: Extension) => allow(ext, allowShowFaxList),
        href: (ext: Extension) => `/learning/result/?_sclass=${ext.id}&fields=פעולה,כתובת%20מייל,קוד%20מסמך,שם%20מסמך,עמודים,מספר%20פקס,סטטוס,קוד%20סטטוס,ניסיונות%20חיוג`
    },
    {
        name:'הוספת נציג לתור',
        action:'add_agent_to_queue',
        description:'action',
        types: allowAddAgentToQueue,
        allow: (ext: Extension) => allow(ext, allowAddAgentToQueue),
        href: (ext: Extension) => `/learning/member/add/`,
    }
];

extensionActions.filterExt = (ext: Extension) => {
    return extensionActions.filter((action) => action.allow(ext));
}

export {
    extensionActions,
}
